import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AdminOnlyGuard } from "@/components/admin/AdminOnlyGuard";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { resolveCarSlug } from "@/lib/car-slug";
import { CheckCircle2, AlertCircle, MinusCircle, PlusCircle, RefreshCw, ExternalLink } from "lucide-react";

export const Route = createFileRoute("/admin/sync-status")({
  head: () => ({
    meta: [
      { title: "Sync-status — AutoXChange" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AdminOnlyGuard>
      <AdminSyncStatus />
    </AdminOnlyGuard>
  ),
});

type SyncLogRow = {
  id: string;
  status: string;
  source: string;
  cars_created: number;
  cars_updated: number;
  cars_marked_sold: number;
  cars_in_feed: number;
  duration_ms: number | null;
  triggered_by: string;
  created_at: string;
  error_message: string | null;
  metadata: {
    mode?: string;
    createdCarIds?: string[];
    updatedCarIds?: string[];
    markedSoldCarIds?: string[];
    itemErrors?: Array<{ bilinfoId: string; op: string; message: string; attempts?: number }>;
    itemErrorCount?: number;
  } | null;
};

type CarInfo = { slug: string; status: string | null; brand: string; model: string };
type CarMap = Record<string, CarInfo>;

function CarLink({ bilinfoId, map }: { bilinfoId: string; map: CarMap }) {
  const info = map[bilinfoId];
  if (!info) {
    return (
      <span className="font-mono text-xs text-muted-foreground" title="Bilen findes ikke længere">
        {bilinfoId}
      </span>
    );
  }
  return (
    <Link
      to="/biler-til-salg/$slug"
      params={{ slug: info.slug }}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm text-foreground hover:underline"
    >
      <span>{info.brand} {info.model}</span>
      <ExternalLink size={12} className="text-muted-foreground" />
    </Link>
  );
}

function StatTile({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number;
  icon: typeof CheckCircle2;
  tone: "success" | "info" | "warn" | "danger";
}) {
  const toneClasses: Record<typeof tone, string> = {
    success: "text-emerald-600 bg-emerald-500/10",
    info: "text-sky-600 bg-sky-500/10",
    warn: "text-amber-600 bg-amber-500/10",
    danger: "text-destructive bg-destructive/10",
  };
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-6">
        <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${toneClasses[tone]}`}>
          <Icon size={22} />
        </div>
        <div>
          <div className="text-3xl font-bold tabular-nums text-foreground">{value}</div>
          <div className="text-sm text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function CarListCard({
  title,
  description,
  ids,
  map,
  emptyText,
}: {
  title: string;
  description: string;
  ids: string[];
  map: CarMap;
  emptyText: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {title} <span className="text-muted-foreground">({ids.length})</span>
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {ids.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyText}</p>
        ) : (
          <ul className="max-h-72 space-y-1 overflow-y-auto pr-2">
            {ids.map((id) => (
              <li key={id} className="flex items-center justify-between border-b border-border/40 py-1.5 last:border-b-0">
                <CarLink bilinfoId={id} map={map} />
                <span className="font-mono text-[10px] text-muted-foreground">{id}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AdminSyncStatus() {
  const { data: latest, isLoading } = useQuery({
    queryKey: ["sync-status-latest"],
    queryFn: async (): Promise<SyncLogRow | null> => {
      const { data, error } = await supabase
        .from("sync_logs")
        .select("*")
        .eq("source", "bilinfo")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as SyncLogRow | null;
    },
    refetchInterval: 15000,
  });

  const meta = latest?.metadata ?? null;
  const createdIds = meta?.createdCarIds ?? [];
  const updatedIds = meta?.updatedCarIds ?? [];
  const soldIds = meta?.markedSoldCarIds ?? [];
  const itemErrors = meta?.itemErrors ?? [];
  const failedIds = useMemo(() => Array.from(new Set(itemErrors.map((e) => e.bilinfoId))), [itemErrors]);

  const allIds = useMemo(
    () => Array.from(new Set([...createdIds, ...updatedIds, ...soldIds, ...failedIds])),
    [createdIds, updatedIds, soldIds, failedIds],
  );

  const { data: carMap } = useQuery({
    queryKey: ["sync-status-car-map", allIds.sort().join(",")],
    enabled: allIds.length > 0,
    queryFn: async (): Promise<CarMap> => {
      const { data, error } = await supabase
        .from("cars")
        .select("id, bilinfo_id, seo_slug, status, brand, model")
        .in("bilinfo_id", allIds);
      if (error) throw error;
      const map: CarMap = {};
      for (const row of data ?? []) {
        if (!row.bilinfo_id) continue;
        map[row.bilinfo_id] = {
          slug: resolveCarSlug(row),
          status: row.status ?? null,
          brand: row.brand,
          model: row.model,
        };
      }
      return map;
    },
    staleTime: 60_000,
  });

  const map = carMap ?? {};

  const skippedCount = latest
    ? Math.max(
        0,
        latest.cars_in_feed -
          latest.cars_created -
          latest.cars_updated -
          latest.cars_marked_sold -
          failedIds.length,
      )
    : 0;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Sync-status</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Resultat fra seneste Bilinfo-synkronisering: hvor mange biler blev opdateret, oprettet, sprunget over eller fejlede.
          </p>
        </div>
        {latest && (
          <div className="text-right text-xs text-muted-foreground">
            <div>Kørt: {new Date(latest.created_at).toLocaleString("da-DK")}</div>
            <div>Trigger: {latest.triggered_by}</div>
            {latest.duration_ms != null && <div>Varighed: {(latest.duration_ms / 1000).toFixed(1)}s</div>}
          </div>
        )}
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Henter seneste sync…</p>}

      {!isLoading && !latest && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Ingen sync-kørsler fundet endnu.
          </CardContent>
        </Card>
      )}

      {latest && (
        <>
          <div className="flex items-center gap-2">
            <Badge variant={latest.status === "success" ? "default" : "destructive"} className="capitalize">
              {latest.status}
            </Badge>
            {meta?.mode && <Badge variant="outline">Mode: {meta.mode}</Badge>}
            <span className="text-sm text-muted-foreground">
              {latest.cars_in_feed} biler i feed
            </span>
          </div>

          {latest.error_message && (
            <Card className="border-destructive/40">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base text-destructive">
                  <AlertCircle size={18} /> Sync-fejl
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono text-xs whitespace-pre-wrap break-words text-destructive">
                  {latest.error_message}
                </p>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <StatTile label="Opdaterede" value={latest.cars_updated} icon={RefreshCw} tone="info" />
            <StatTile label="Oprettede" value={latest.cars_created} icon={PlusCircle} tone="success" />
            <StatTile label="Markeret solgt" value={latest.cars_marked_sold} icon={MinusCircle} tone="warn" />
            <StatTile label="Sprunget over" value={skippedCount} icon={CheckCircle2} tone="success" />
            <StatTile label="Fejlede" value={failedIds.length} icon={AlertCircle} tone="danger" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <CarListCard
              title="Opdaterede biler"
              description={
                latest.cars_updated > updatedIds.length
                  ? `Viser de første ${updatedIds.length} af ${latest.cars_updated}`
                  : "Biler hvor data blev opdateret fra feedet"
              }
              ids={updatedIds}
              map={map}
              emptyText="Ingen biler blev opdateret i denne kørsel."
            />
            <CarListCard
              title="Oprettede biler"
              description={
                latest.cars_created > createdIds.length
                  ? `Viser de første ${createdIds.length} af ${latest.cars_created}`
                  : "Nye biler tilføjet fra feedet"
              }
              ids={createdIds}
              map={map}
              emptyText="Ingen nye biler i denne kørsel."
            />
            <CarListCard
              title="Markeret som solgt"
              description="Biler fjernet fra feedet og sat til solgt"
              ids={soldIds}
              map={map}
              emptyText="Ingen biler blev markeret som solgt."
            />
            <Card className={failedIds.length > 0 ? "border-destructive/40" : undefined}>
              <CardHeader>
                <CardTitle className="text-base">
                  Fejlede biler <span className="text-muted-foreground">({failedIds.length})</span>
                </CardTitle>
                <CardDescription>Per-bil fejl under sync (fra feedet til DB)</CardDescription>
              </CardHeader>
              <CardContent>
                {itemErrors.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Ingen fejl i denne kørsel.</p>
                ) : (
                  <ul className="max-h-72 space-y-2 overflow-y-auto pr-2">
                    {itemErrors.map((e, i) => (
                      <li key={`${e.bilinfoId}-${i}`} className="border-b border-border/40 pb-2 last:border-b-0">
                        <div className="flex items-center justify-between gap-2">
                          <CarLink bilinfoId={e.bilinfoId} map={map} />
                          <Badge variant="outline" className="text-[10px]">{e.op}</Badge>
                        </div>
                        <p className="mt-1 font-mono text-[11px] text-destructive break-words">{e.message}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
